import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  useCancelProjectOwnershipTransferMutation,
  useInitiateProjectOwnershipTransferMutation,
  useProjectOwnershipTransfer,
  useResendProjectOwnershipTransferEmailMutation,
  useUpdateProjectOwnershipTransferEmailMutation,
} from '@/features/projects/project-details/hooks/use-project-ownership-transfer';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { DialogHeader } from '@/shared/components/ui/dialog/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/shared/components/ui/form';
import { LabelInput } from '@/shared/components/ui/label-input';
import { toast } from '@/shared/hooks/use-toast';
import {
  handleError,
  useErrorTranslation,
} from '@/shared/utils/error-handling';

const transferEmailSchema = z.object({
  email: z.string().email('invalid'),
});

type TransferEmailInput = z.infer<typeof transferEmailSchema>;

interface ProjectOwnershipTransferDialogProps {
  projectId: number;
  projectName: string;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

export const ProjectOwnershipTransferDialog = ({
  projectId,
  projectName,
  isOpen,
  onOpenChange,
}: ProjectOwnershipTransferDialogProps) => {
  const { t } = useTranslation('project', {
    keyPrefix: 'ownership_transfer_dialog',
  });
  const { t: commonT } = useTranslation('common');
  const translateError = useErrorTranslation();

  const { data: existingTransfer, isLoading: isLoadingTransfer } =
    useProjectOwnershipTransfer({ projectId });

  const form = useForm<TransferEmailInput>({
    resolver: zodResolver(transferEmailSchema),
    defaultValues: { email: existingTransfer?.to_email ?? '' },
  });

  const { mutate: initiateTransfer, isPending: isInitiating } =
    useInitiateProjectOwnershipTransferMutation();

  const { mutate: resendEmail, isPending: isResending } =
    useResendProjectOwnershipTransferEmailMutation(projectId);

  const { mutate: updateEmail, isPending: isUpdating } =
    useUpdateProjectOwnershipTransferEmailMutation(projectId);

  const { mutate: cancelTransfer, isPending: isCancelling } =
    useCancelProjectOwnershipTransferMutation(projectId);

  const isPending = isInitiating || isResending || isUpdating || isCancelling;

  const handleInitiateTransfer = (input: TransferEmailInput) => {
    initiateTransfer(
      { projectId, toEmail: input.email },
      {
        onSuccess: () => {
          toast({ title: t('success.initiated') });
          onOpenChange(false);
        },
        onError: (error) => {
          const message =
            error instanceof Error ? error.message : 'Unknown error';
          if (message.includes('transfer_already_pending')) {
            toast({
              title: t('error.already_pending'),
              variant: 'destructive',
            });
          } else {
            toast({
              title: t('error.generic'),
              variant: 'destructive',
            });
          }
        },
      },
    );
  };

  const handleResendEmail = () => {
    if (!existingTransfer) return;
    resendEmail(
      { transferId: existingTransfer.id },
      {
        onSuccess: () => {
          toast({ title: t('success.resent') });
        },
        onError: async (error) => {
          const appError = await handleError(error);
          toast({
            title: translateError(appError),
            variant: 'destructive',
          });
        },
      },
    );
  };

  const handleUpdateEmail = (input: TransferEmailInput) => {
    if (!existingTransfer) return;
    updateEmail(
      { transferId: existingTransfer.id, newEmail: input.email },
      {
        onSuccess: () => {
          toast({ title: t('success.email_updated') });
        },
        onError: () => {
          toast({
            title: t('error.generic'),
            variant: 'destructive',
          });
        },
      },
    );
  };

  const handleCancelTransfer = () => {
    if (!existingTransfer) return;
    cancelTransfer(
      { transferId: existingTransfer.id },
      {
        onSuccess: () => {
          toast({ title: t('success.cancelled') });
          onOpenChange(false);
        },
        onError: () => {
          toast({
            title: t('error.generic'),
            variant: 'destructive',
          });
        },
      },
    );
  };

  const handleSubmit = (input: TransferEmailInput) => {
    if (existingTransfer) {
      if (input.email !== existingTransfer.to_email) {
        handleUpdateEmail(input);
      } else {
        handleResendEmail();
      }
    } else {
      handleInitiateTransfer(input);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader className="mb-6">
          <DialogTitle>
            {existingTransfer ? t('title_pending') : t('title')}
          </DialogTitle>
        </DialogHeader>
        <DialogDescription className="mb-6">
          {existingTransfer
            ? t('description_pending', { email: existingTransfer.to_email })
            : t('description', { name: projectName })}
        </DialogDescription>

        {isLoadingTransfer ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="space-y-6"
              noValidate
            >
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <LabelInput
                        label={commonT('form_field.email.name')}
                        type="email"
                        placeholder={commonT('form_field.email.placeholder')}
                        error={!!form.formState.errors.email}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage
                      t={(key) =>
                        commonT([
                          `form_field.email.errors.${key}` as any,
                          'form_field.email.errors.unknown',
                        ])
                      }
                    />
                  </FormItem>
                )}
              />

              <DialogFooter className="mt-6 gap-2">
                {existingTransfer && (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={handleCancelTransfer}
                    disabled={isPending}
                    className="mr-auto"
                  >
                    {isCancelling ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      t('actions.cancel_transfer')
                    )}
                  </Button>
                )}
                <DialogClose asChild>
                  <Button type="button" variant="outline" disabled={isPending}>
                    {t('actions.close')}
                  </Button>
                </DialogClose>
                <Button
                  type="submit"
                  variant="greenGradient"
                  disabled={isPending}
                  className="min-w-[140px]"
                >
                  {isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : existingTransfer ? (
                    form.watch('email') !== existingTransfer.to_email ? (
                      t('actions.update_and_resend')
                    ) : (
                      t('actions.resend')
                    )
                  ) : (
                    t('actions.transfer')
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};
